import type { Session } from "@supabase/supabase-js";
import { useEffect, useState } from "react";
import { AdminAccess } from "./AdminAccess";
import {
  getAdminSupabaseClient,
  readAdminSession,
  signOutAdmin,
} from "./lib/adminRepository";
import {
  loadSystemHealth,
  type SystemHealthSnapshot,
} from "./lib/systemHealthRepository";
import "./system-health.css";

export function SystemHealthWorkspaceAccess() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    readAdminSession()
      .then((value) => active && setSession(value))
      .finally(() => active && setLoading(false));
    const { data } = getAdminSupabaseClient().auth.onAuthStateChange(
      (_event, nextSession) => {
        if (active) {
          setSession(nextSession);
          setLoading(false);
        }
      },
    );
    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, []);

  if (loading) {
    return <div className="health-loading">Systemcheck wird geöffnet …</div>;
  }
  if (!session) return <AdminAccess />;
  return <SystemHealthWorkspace />;
}

function SystemHealthWorkspace() {
  const [snapshot, setSnapshot] = useState<SystemHealthSnapshot | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    setBusy(true);
    setError(null);
    try {
      setSnapshot(await loadSystemHealth());
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Der Systemcheck ist fehlgeschlagen.",
      );
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  return (
    <main className="health-shell">
      <header className="health-header">
        <div>
          <p className="health-kicker">Concierge OS</p>
          <h1>System Health</h1>
          <p>Ein gemeinsamer Blick auf Datenbank, Buchung, Kalender und E-Mail.</p>
        </div>
        <div className="health-actions">
          <a href="/?admin=1">Zur Verwaltung</a>
          <button type="button" onClick={() => void refresh()} disabled={busy}>
            {busy ? "Prüfe …" : "Neu prüfen"}
          </button>
          <button type="button" onClick={() => void signOutAdmin()}>
            Abmelden
          </button>
        </div>
      </header>

      {error ? <div className="health-error">{error}</div> : null}
      {!snapshot && !error ? <p>System wird geprüft …</p> : null}

      {snapshot ? (
        <>
          <section className={`health-overall ${snapshot.status}`}>
            <span>{statusIcon(snapshot.status)}</span>
            <div>
              <strong>{statusLabel(snapshot.status)}</strong>
              <small>
                Stand: {new Date(snapshot.generatedAt).toLocaleString("de-DE")}
              </small>
            </div>
          </section>
          <section className="health-grid">
            {snapshot.checks.map((check) => (
              <article key={check.id} className={`health-card ${check.status}`}>
                <div className="health-card-title">
                  <span>{statusIcon(check.status)}</span>
                  <h2>{check.label}</h2>
                </div>
                <p>{check.summary}</p>
                {check.detail ? <small>{check.detail}</small> : null}
              </article>
            ))}
          </section>
        </>
      ) : null}
    </main>
  );
}

function statusIcon(status: SystemHealthSnapshot["status"]): string {
  if (status === "healthy") return "●";
  if (status === "warning") return "▲";
  return "!";
}

function statusLabel(status: SystemHealthSnapshot["status"]): string {
  if (status === "healthy") return "Alle Kernsysteme sind gesund";
  if (status === "warning") return "System läuft mit Hinweisen";
  return "Mindestens ein Kernsystem braucht Aufmerksamkeit";
}
